import { useRef, useEffect, useLayoutEffect } from "react";
import { useField } from "formik";
import { Input, Label } from "reactstrap";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import ErrorMessageDefault from "../../base/components/ErrorMessageDefault";
import EmojiPicker from "../EmojiPicker";

import { getMaxLength } from "@helpers/validation";

import styles from "./index.module.scss";

const FormikTextareaResizable = ({
  label,
  disabled,
  name,
  min,
  maxLength,
  readOnly,
  className,
  hideError,
  wrapperClassName,
  placeholder,
  textLabel,
  errorLineShow,
  rows = 2,
  maxRows: propsMaxRows,
  autoFocus,
  isEmoji = false,
  placement,
  maxHeight = 250,
  minHeight = 70,
  emojiPopperClassName = "",
  withoutTouch,
}) => {
  const maxRows = propsMaxRows ?? rows;
  const textAreaRef = useRef(null);
  const { t } = useTranslation("placeholders");
  const { t: t1 } = useTranslation("labels");
  const { t: t2 } = useTranslation("errors");
  const [{ onChange, value, ...field }, meta, { setValue }] = useField({
    name,
    type: "text",
    placeholder,
  });

  const isInvalid = withoutTouch
    ? meta.error && errorLineShow
    : meta.error && meta.touched && errorLineShow;

  const isStringLabel = typeof label === "string";

  const isTextAreaScrollable =
    textAreaRef?.current?.scrollHeight > textAreaRef?.current?.clientHeight &&
    !propsMaxRows;

  const getMaxHeight = () => {
    const textarea = textAreaRef.current;
    const computedStyles = getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyles.lineHeight);

    if (!propsMaxRows) return maxHeight;

    return (
      lineHeight * maxRows +
      parseFloat(computedStyles.paddingTop) +
      parseFloat(computedStyles.paddingBottom)
    );
  };

  const adjustHeight = () => {
    const textarea = textAreaRef.current;
    textarea.style.height = "auto"; // Reset to recalculate
    const scrollHeight = textarea.scrollHeight;
    // Calculate max height based on max rows
    const _maxHeight = getMaxHeight();
    textarea.style.height = `${Math.min(scrollHeight, _maxHeight)}px`;
  };

  useLayoutEffect(adjustHeight, [value]);

  const willWrapToNextLine = (currentText, nextChar) => {
    const textarea = textAreaRef.current;

    // Simulate new text input
    const newText = currentText + nextChar;
    const computedStyles = getComputedStyle(textarea);
    // Temporarily apply it to check height change
    const tempTextarea = textarea.cloneNode(true);
    tempTextarea.style.position = "absolute";
    tempTextarea.style.visibility = "hidden";
    tempTextarea.style.height = "auto";
    tempTextarea.style.width = textarea.clientWidth + "px";
    tempTextarea.style.font = computedStyles.font;
    tempTextarea.style.lineHeight = computedStyles.lineHeight;
    tempTextarea.style.whiteSpace = "pre-wrap";
    tempTextarea.style.overflowWrap = "break-word";
    tempTextarea.rows = textarea.rows;
    tempTextarea.value = newText;

    document.body.appendChild(tempTextarea);
    const newHeight = tempTextarea.scrollHeight;
    document.body.removeChild(tempTextarea);

    // compare with correctly calculated max height (including padding)
    return newHeight > getMaxHeight();
  };

  const handleChange = (event) => {
    const textarea = textAreaRef.current;
    const newText = event.target.value;

    // if next char will wrap to next line, prevent it
    if (
      propsMaxRows &&
      newText.length > value.length &&
      willWrapToNextLine(value, newText[textarea.selectionStart - 1])
    ) {
      return;
    }

    onChange(event);
  };

  const maxCalculatedLength = getMaxLength(value, maxLength);

  const checkIsErrorMessageVisible = () => {
    if (withoutTouch) {
      return !hideError;
    }
    return !hideError && meta.touched;
  };

  // support maxLength for mobile
  useEffect(() => {
    if (!value) return;
    if (value?.length <= maxCalculatedLength) return;
    setValue(value.slice(0, maxCalculatedLength));
  }, [value, setValue, maxCalculatedLength]);

  return (
    <section className={classNames("form-control-wrapper", wrapperClassName)}>
      {label && (
        <Label for={name} className={classNames({ disabled }, styles.label)}>
          {isStringLabel ? t1(label) : label}
        </Label>
      )}
      <div className="position-relative">
        <Input
          {...field}
          autoFocus={autoFocus}
          innerRef={textAreaRef}
          rows={rows}
          disabled={disabled}
          value={value}
          className={classNames(
            "form-control auto text-emoji-regular",
            className,
            isTextAreaScrollable ? "overflow-y-auto" : "overflow-hidden",
            {
              invalid: isInvalid,
            },
          )}
          name={name}
          style={{ minHeight }}
          onChange={handleChange}
          placeholder={t(placeholder)}
          type="textarea"
          readOnly={readOnly}
          maxLength={maxCalculatedLength}
        />
        {isEmoji && (
          <EmojiPicker
            emojiClassName={styles.textfieldEmoji}
            value={value}
            setValue={setValue}
            inputRef={textAreaRef}
            placement={placement}
            disabled={value?.length >= maxLength || disabled}
            popperClassName={emojiPopperClassName}
          />
        )}
      </div>
      {checkIsErrorMessageVisible() ? (
        <ErrorMessageDefault
          className="position-absolute bottom-xl-0"
          name={name}
        >
          {t2(meta.error, {
            label: isStringLabel ? t1(label) : t1(textLabel),
            min,
            max: maxLength,
            labelLowerCased: (isStringLabel && t1(label).toLowerCase()) || "",
          })}
        </ErrorMessageDefault>
      ) : null}
    </section>
  );
};

export default FormikTextareaResizable;
